import React, { useEffect, useState } from "react";
import axios from "axios";
import { useApp } from "../contexts/AppContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { 
  Plus, Pencil, Trash2, Search, Users,
  Mail, Phone, Building2, MapPin, UserX, UserCheck, RotateCcw, KeyRound, Upload, FileUp, FileText, Download, Eye
} from "lucide-react";
import { formatCurrency, LOCATION_TYPES, API_URL } from "../lib/utils";
import { useAuth } from "../contexts/AuthContext";
import { Checkbox } from "../components/ui/checkbox";


const EMP_DOC_CATEGORIES = [
  "Busta Paga", "CU", "Antincendio", "Primo Soccorso", "HACCP",
  "Formazione Sicurezza 81/08", "Documento Identità", "Contratto Assunzione", "Altro"
];

export default function EmployeesPage() {
  const { isAdmin } = useAuth();
  const { companies, locations, fetchCompanies, fetchLocations } = useApp();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCompany, setFilterCompany] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [showTerminated, setShowTerminated] = useState(false);
  const [terminateDialog, setTerminateDialog] = useState(null);
  const [terminateForm, setTerminateForm] = useState({ termination_date: "", termination_reason: "" });
  const [docsDialog, setDocsDialog] = useState(null);
  const [empDocs, setEmpDocs] = useState([]);
  const [uploadDocEmp, setUploadDocEmp] = useState(null);
  const [uploadDocForm, setUploadDocForm] = useState({ category: "", description: "", file: null });
  const [docsFilterCat, setDocsFilterCat] = useState("");
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    codice_fiscale: "",
    iban: "",
    phone: "",
    company_id: "",
    location_id: "",
    department: "",
    role: "",
    classification: "",
    contract_hours: "",
    hourly_rate: "",
    base_salary: "",
    notes: "",
    is_ghost: false
  });

  useEffect(() => {
    fetchCompanies();
    fetchLocations();
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCompanies, fetchLocations]);

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCompany, filterLocation, search, showTerminated]);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCompany) params.company_id = filterCompany;
      if (filterLocation) params.location_id = filterLocation;
      if (search) params.search = search;
      if (showTerminated) params.include_terminated = true;
      const response = await axios.get(`${API_URL}/api/employees`, { params, withCredentials: true });
      setEmployees(response.data);
    } catch (err) {
      console.error("Error fetching employees:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (employee = null) => {
    if (employee) {
      setEditingEmployee(employee);
      setFormData({
        first_name: employee.first_name || "",
        last_name: employee.last_name || "",
        email: employee.email || "",
        codice_fiscale: employee.codice_fiscale || "",
        iban: employee.iban || "",
        phone: employee.phone || "",
        company_id: employee.company_id || "",
        location_id: employee.location_id || "",
        department: employee.department || "",
        role: employee.role || "",
        classification: employee.classification || "",
        contract_hours: employee.contract_hours?.toString() || "",
        hourly_rate: employee.hourly_rate?.toString() || "",
        base_salary: employee.base_salary?.toString() || "",
        notes: employee.notes || "",
        is_ghost: employee.is_ghost || false
      });
    } else {
      setEditingEmployee(null);
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        codice_fiscale: "",
        iban: "",
        phone: "",
        company_id: filterCompany || "",
        location_id: filterLocation || "",
        department: "",
        role: "",
        classification: "",
        contract_hours: "",
        hourly_rate: "",
        base_salary: "",
        notes: "",
        is_ghost: false
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const data = {
        ...formData,
        contract_hours: formData.contract_hours ? parseFloat(formData.contract_hours) : null,
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
        base_salary: formData.base_salary ? parseFloat(formData.base_salary) : null,
        email: formData.email || null
      };

      if (editingEmployee) {
        await axios.put(`${API_URL}/api/employees/${editingEmployee.id}`, data, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/employees`, data, { withCredentials: true });
      }
      setDialogOpen(false);
      fetchEmployees();
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo dipendente?")) return;
    try {
      await axios.delete(`${API_URL}/api/employees/${id}`, { withCredentials: true });
      fetchEmployees();
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    }
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company?.name || "-";
  };

  const getLocationName = (locationId) => {
    const location = locations.find(l => l.id === locationId);
    return location?.name || "-";
  };

  const filteredLocations = filterCompany 
    ? locations.filter(l => l.company_id === filterCompany)
    : locations;

  const handleTerminate = async () => {
    if (!terminateDialog) return;
    try {
      await axios.post(`${API_URL}/api/employees/${terminateDialog.id}/terminate`, {
        termination_date: terminateForm.termination_date || new Date().toISOString().split("T")[0],
        termination_reason: terminateForm.termination_reason
      }, { withCredentials: true });
      setTerminateDialog(null);
      setTerminateForm({ termination_date: "", termination_reason: "" });
      fetchEmployees();
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleReactivate = async (empId) => {
    try {
      await axios.post(`${API_URL}/api/employees/${empId}/reactivate`, {}, { withCredentials: true });
      fetchEmployees();
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    }
  };

  const activeCount = employees.filter(e => e.is_active !== false).length;
  const terminatedCount = employees.filter(e => e.is_active === false).length;

  const handleResetPin = async (empId) => {
    if (!window.confirm("Resettare il PIN di questo dipendente a 123456?")) return;
    try {
      await axios.post(`${API_URL}/api/portal/reset-pin/${empId}`, {}, { withCredentials: true });
      alert("PIN resettato. Il dipendente dovrà usare 123456 al prossimo accesso.");
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleUploadCF = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API_URL}/api/employees/upload-cf`, fd, { withCredentials: true });
      alert(`${res.data.updated} codici fiscali aggiornati.${res.data.errors?.length ? "\nErrori:\n" + res.data.errors.join("\n") : ""}`);
      fetchEmployees();
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    }
    e.target.value = "";
  };

  const handleUploadDoc = async (empId, file, desc, category) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("employee_id", empId);
    fd.append("description", desc || file.name);
    if (category) fd.append("category", category);
    try {
      await axios.post(`${API_URL}/api/portal/documents/upload`, fd, { withCredentials: true });
      alert("Documento caricato");
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleUploadSharedDoc = async (file, desc) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("shared", "true");
    fd.append("description", desc || file.name);
    try {
      await axios.post(`${API_URL}/api/portal/documents/upload`, fd, { withCredentials: true });
      alert("Documento condiviso caricato");
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleShowDocs = async (emp) => {
    setDocsDialog(emp);
    try {
      const res = await axios.get(`${API_URL}/api/portal/documents/${emp.id}`, { withCredentials: true });
      setEmpDocs([...(res.data.personal || []), ...(res.data.shared || []).map(d => ({ ...d, _shared: true }))]);
    } catch (err) {
      setEmpDocs([]);
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm("Eliminare questo documento?")) return;
    try {
      await axios.delete(`${API_URL}/api/portal/documents/${docId}`, { withCredentials: true });
      if (docsDialog) handleShowDocs(docsDialog);
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    }
  };


  const formLocations = formData.company_id
    ? locations.filter(l => l.company_id === formData.company_id)
    : locations;

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-outfit tracking-tight text-[#09090B]">
            Dipendenti
          </h1>
          <p className="text-[#52525B] font-ibm-plex mt-1">
            Gestisci l'anagrafica dei dipendenti
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            data-testid="add-employee-button"
            onClick={() => handleOpenDialog()}
            className="bg-black hover:bg-gray-800 text-white font-ibm-plex"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuovo Dipendente
          </Button>
          {isAdmin && (
            <>
              <Button variant="outline" className="font-ibm-plex text-xs relative" data-testid="upload-cf-btn" asChild>
                <label className="cursor-pointer">
                  <Upload className="w-4 h-4 mr-1" /> Carica CF
                  <input type="file" accept=".csv,.txt,.xlsx,.xls" className="hidden" onChange={handleUploadCF} />
                </label>
              </Button>
              <Button variant="outline" className="font-ibm-plex text-xs relative" data-testid="upload-shared-doc-btn" asChild>
                <label className="cursor-pointer">
                  <FileUp className="w-4 h-4 mr-1" /> Doc. Condiviso
                  <input type="file" className="hidden" onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const desc = prompt("Descrizione documento:", file.name);
                      if (desc !== null) handleUploadSharedDoc(file, desc);
                    }
                    e.target.value = "";
                  }} />
                </label>
              </Button>
              <Button variant="outline" className="font-ibm-plex text-xs relative" data-testid="upload-payslips-btn" asChild>
                <label className="cursor-pointer">
                  <Upload className="w-4 h-4 mr-1" /> Buste Paga (ZIP)
                  <input type="file" accept=".zip" className="hidden" onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    const monthLabel = prompt("Etichetta mese (es: Aprile 2025):");
                    if (!monthLabel) { e.target.value = ""; return; }
                    const fd = new FormData();
                    fd.append("file", file);
                    fd.append("month_label", monthLabel);
                    try {
                      const res = await axios.post(`${API_URL}/api/payslips/upload-zip`, fd, { withCredentials: true });
                      alert(`${res.data.assigned} buste paga assegnate.${res.data.errors?.length ? "\nErrori:\n" + res.data.errors.join("\n") : ""}`);
                    } catch (err) {
                      alert("Errore: " + (err.response?.data?.detail || err.message));
                    }
                    e.target.value = "";
                  }} />
                </label>
              </Button>
              <Button variant="outline" className="font-ibm-plex text-xs" data-testid="send-payslips-btn" onClick={async () => {
                const monthLabel = prompt("Mese delle buste paga da inviare (es: Aprile 2025):");
                if (!monthLabel) return;
                if (!window.confirm(`Inviare via email le buste paga di "${monthLabel}" a tutti i dipendenti?`)) return;
                try {
                  const res = await axios.post(`${API_URL}/api/payslips/send-emails`, { month_label: monthLabel }, { withCredentials: true });
                  alert(`${res.data.sent} email inviate.${res.data.errors?.length ? "\nErrori:\n" + res.data.errors.join("\n") : ""}`);
                } catch (err) {
                  alert("Errore: " + (err.response?.data?.detail || err.message));
                }
              }}>
                <Mail className="w-4 h-4 mr-1" /> Invia Buste Paga
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="border border-[#E4E4E7]">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                data-testid="search-employees-input"
                placeholder="Cerca dipendente..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 font-ibm-plex"
              />
            </div>
            <Select value={filterCompany || "all"} onValueChange={(v) => { setFilterCompany(v === "all" ? "" : v); setFilterLocation(""); }}>
              <SelectTrigger data-testid="filter-company-select" className="font-ibm-plex">
                <SelectValue placeholder="Tutte le aziende" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le aziende</SelectItem>
                {companies.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterLocation || "all"} onValueChange={(v) => setFilterLocation(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="filter-location-select" className="font-ibm-plex">
                <SelectValue placeholder="Tutte le sedi" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le sedi</SelectItem>
                {filteredLocations.map(l => (
                  <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center justify-end text-sm text-[#52525B] font-ibm-plex">
              <Users className="w-4 h-4 mr-2" />
              {activeCount} attivi{showTerminated && terminatedCount > 0 ? ` / ${terminatedCount} cessati` : ""}
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                data-testid="show-terminated-toggle"
                checked={showTerminated}
                onCheckedChange={(checked) => setShowTerminated(!!checked)}
              />
              <label className="text-sm font-ibm-plex text-[#52525B] cursor-pointer" onClick={() => setShowTerminated(!showTerminated)}>
                Mostra cessati
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border border-[#E4E4E7]">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F4F4F5]">
                  <TableHead className="font-ibm-plex font-semibold">Nome</TableHead>
                  <TableHead className="font-ibm-plex font-semibold">Codice Fiscale</TableHead>
                  <TableHead className="font-ibm-plex font-semibold">Azienda</TableHead>
                  <TableHead className="font-ibm-plex font-semibold">Sede</TableHead>
                  <TableHead className="font-ibm-plex font-semibold">Reparto</TableHead>
                  <TableHead className="font-ibm-plex font-semibold text-right">Ore Contratto</TableHead>
                  <TableHead className="font-ibm-plex font-semibold text-right">Paga Oraria</TableHead>
                  <TableHead className="font-ibm-plex font-semibold text-center">Stato</TableHead>
                  <TableHead className="font-ibm-plex font-semibold w-[120px]">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-[#52525B]">
                      Caricamento...
                    </TableCell>
                  </TableRow>
                ) : employees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-[#52525B]">
                      Nessun dipendente trovato
                    </TableCell>
                  </TableRow>
                ) : (
                  employees.map(emp => {
                    const isTerminated = emp.is_active === false;
                    return (
                    <TableRow key={emp.id} className={`hover:bg-[#F4F4F5] ${isTerminated ? "opacity-50 bg-gray-50" : ""}`} data-testid={`employee-row-${emp.id}`}>
                      <TableCell className="font-ibm-plex font-medium">
                        {emp.last_name} {emp.first_name}
                      </TableCell>
                      <TableCell className="font-ibm-plex-mono text-xs text-[#52525B]">
                        {emp.codice_fiscale || "-"}
                      </TableCell>
                      <TableCell className="font-ibm-plex text-[#52525B]">
                        {getCompanyName(emp.company_id)}
                      </TableCell>
                      <TableCell className="font-ibm-plex text-[#52525B]">
                        {getLocationName(emp.location_id)}
                      </TableCell>
                      <TableCell className="font-ibm-plex text-[#52525B]">
                        {emp.department || "-"}
                      </TableCell>
                      <TableCell className="font-ibm-plex-mono text-right">
                        {emp.contract_hours || "-"}
                      </TableCell>
                      <TableCell className="font-ibm-plex-mono text-right">
                        {emp.hourly_rate ? formatCurrency(emp.hourly_rate) : "-"}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex flex-col items-center gap-1">
                          {isTerminated ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800" data-testid={`status-terminated-${emp.id}`}>
                              <UserX className="w-3 h-3" />
                              Cessato
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-testid={`status-active-${emp.id}`}>
                              <UserCheck className="w-3 h-3" />
                              Attivo
                            </span>
                          )}
                          {emp.is_ghost && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-[#27272A] text-white" data-testid={`status-ghost-${emp.id}`}>
                              GHOST
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`edit-employee-${emp.id}`}
                            onClick={() => handleOpenDialog(emp)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {isTerminated ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              data-testid={`reactivate-employee-${emp.id}`}
                              onClick={() => handleReactivate(emp.id)}
                              title="Riattiva dipendente"
                              className="text-green-600 hover:text-green-800"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              data-testid={`terminate-employee-${emp.id}`}
                              onClick={() => {
                                setTerminateDialog(emp);
                                setTerminateForm({ termination_date: new Date().toISOString().split("T")[0], termination_reason: "" });
                              }}
                              title="Cessa dipendente"
                              className="text-amber-600 hover:text-amber-800"
                            >
                              <UserX className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`delete-employee-${emp.id}`}
                            onClick={() => handleDelete(emp.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          {isAdmin && (
                            <>
                              <Button variant="ghost" size="icon" title="Reset PIN" onClick={() => handleResetPin(emp.id)} className="text-[#71717A]" data-testid={`reset-pin-${emp.id}`}>
                                <KeyRound className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" title="Carica documento" className="text-[#71717A]"
                                onClick={() => {
                                  setUploadDocEmp(emp);
                                }}
                                data-testid={`upload-doc-${emp.id}`}
                              >
                                <FileUp className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" title="Vedi allegati" onClick={() => handleShowDocs(emp)} className="text-[#71717A]" data-testid={`view-docs-${emp.id}`}>
                                <FileText className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit">
              {editingEmployee ? "Modifica Dipendente" : "Nuovo Dipendente"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label className="font-ibm-plex">Nome *</Label>
              <Input
                data-testid="employee-firstname-input"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                className="font-ibm-plex"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Cognome *</Label>
              <Input
                data-testid="employee-lastname-input"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                className="font-ibm-plex"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Email</Label>
              <Input
                data-testid="employee-email-input"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="font-ibm-plex"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Codice Fiscale</Label>
              <Input
                data-testid="employee-cf-input"
                value={formData.codice_fiscale}
                onChange={(e) => setFormData({ ...formData, codice_fiscale: e.target.value.toUpperCase() })}
                className="font-ibm-plex-mono tracking-wider"
                maxLength={16}
                placeholder="RSSMRA85M01H501Z"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Telefono</Label>
              <Input
                data-testid="employee-phone-input"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="font-ibm-plex"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Azienda *</Label>
              <Select 
                value={formData.company_id} 
                onValueChange={(v) => setFormData({ ...formData, company_id: v, location_id: "" })}
              >
                <SelectTrigger data-testid="employee-company-select" className="font-ibm-plex">
                  <SelectValue placeholder="Seleziona azienda" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Sede *</Label>
              <Select value={formData.location_id} onValueChange={(v) => setFormData({ ...formData, location_id: v })}>
                <SelectTrigger data-testid="employee-location-select" className="font-ibm-plex">
                  <SelectValue placeholder="Seleziona sede" />
                </SelectTrigger>
                <SelectContent>
                  {formLocations.map(l => (
                    <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Reparto / Mansione</Label>
              <Input
                data-testid="employee-department-input"
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                className="font-ibm-plex"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Inquadramento</Label>
              <Input
                data-testid="employee-classification-input"
                value={formData.classification}
                onChange={(e) => setFormData({ ...formData, classification: e.target.value })}
                className="font-ibm-plex"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">IBAN</Label>
              <Input
                data-testid="employee-iban-input"
                value={formData.iban}
                onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                className="font-ibm-plex"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Ore Contrattuali</Label>
              <Input
                data-testid="employee-hours-input"
                type="number"
                value={formData.contract_hours}
                onChange={(e) => setFormData({ ...formData, contract_hours: e.target.value })}
                className="font-ibm-plex"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Paga Oraria (€)</Label>
              <Input
                data-testid="employee-hourly-rate-input"
                type="number"
                step="0.01"
                value={formData.hourly_rate}
                onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                className="font-ibm-plex"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Stipendio Base (€)</Label>
              <Input
                data-testid="employee-salary-input"
                type="number"
                step="0.01"
                value={formData.base_salary}
                onChange={(e) => setFormData({ ...formData, base_salary: e.target.value })}
                className="font-ibm-plex"
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label className="font-ibm-plex">Note</Label>
              <Textarea
                data-testid="employee-notes-input"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="font-ibm-plex"
                rows={3}
              />
            </div>
            <div className="col-span-2 flex items-center gap-3 pt-2 border-t border-[#E4E4E7]">
              <Checkbox
                id="ghost-checkbox"
                data-testid="employee-ghost-checkbox"
                checked={formData.is_ghost}
                onCheckedChange={(checked) => setFormData({ ...formData, is_ghost: !!checked })}
              />
              <Label htmlFor="ghost-checkbox" className="font-ibm-plex text-sm cursor-pointer">
                <span className="font-semibold">Indennità</span>
                <span className="text-[#71717A] ml-1">— Escludi da tutti i PDF (commercialista)</span>
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="font-ibm-plex">
              Annulla
            </Button>
            <Button 
              data-testid="save-employee-button"
              onClick={handleSave} 
              className="bg-black hover:bg-gray-800 text-white font-ibm-plex"
              disabled={!formData.first_name || !formData.last_name || !formData.company_id || !formData.location_id}
            >
              {editingEmployee ? "Salva Modifiche" : "Crea Dipendente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Terminate Dialog */}
      <Dialog open={!!terminateDialog} onOpenChange={(open) => { if (!open) setTerminateDialog(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2 text-amber-700">
              <UserX className="w-5 h-5" />
              Cessazione Dipendente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm font-ibm-plex text-[#52525B]">
              Stai cessando <span className="font-semibold text-[#09090B]">{terminateDialog?.last_name} {terminateDialog?.first_name}</span>. 
              Il dipendente non comparirà più nelle liste attive ma resterà nello storico.
            </p>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Data Cessazione</Label>
              <Input
                data-testid="terminate-date-input"
                type="date"
                value={terminateForm.termination_date}
                onChange={(e) => setTerminateForm({ ...terminateForm, termination_date: e.target.value })}
                className="font-ibm-plex"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Motivo Cessazione</Label>
              <Textarea
                data-testid="terminate-reason-input"
                value={terminateForm.termination_reason}
                onChange={(e) => setTerminateForm({ ...terminateForm, termination_reason: e.target.value })}
                className="font-ibm-plex"
                placeholder="Es: Dimissioni volontarie, Fine contratto, Licenziamento..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTerminateDialog(null)} className="font-ibm-plex">
              Annulla
            </Button>
            <Button
              data-testid="confirm-terminate-button"
              onClick={handleTerminate}
              className="bg-amber-600 hover:bg-amber-700 text-white font-ibm-plex"
            >
              Conferma Cessazione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Documents Dialog */}
      <Dialog open={!!docsDialog} onOpenChange={(open) => { if (!open) { setDocsDialog(null); setDocsFilterCat(""); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-outfit">Documenti di {docsDialog?.last_name} {docsDialog?.first_name}</DialogTitle>
          </DialogHeader>
          <div className="flex gap-2 mb-2">
            <Select value={docsFilterCat || "all"} onValueChange={(v) => setDocsFilterCat(v === "all" ? "" : v)}>
              <SelectTrigger className="font-ibm-plex text-xs h-8 w-48">
                <SelectValue placeholder="Tutte le categorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le categorie</SelectItem>
                {EMP_DOC_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="max-h-[400px] overflow-y-auto space-y-2 py-2">
            {empDocs.filter(d => !docsFilterCat || d.category === docsFilterCat).length === 0 ? (
              <p className="text-center text-sm text-[#71717A] font-ibm-plex py-4">Nessun documento</p>
            ) : (
              empDocs.filter(d => !docsFilterCat || d.category === docsFilterCat).map((d) => (
                <div key={d.id} className="flex items-center justify-between py-2 px-3 rounded-lg border border-[#E4E4E7] hover:bg-[#F4F4F5]">
                  <div className="flex-1 min-w-0 mr-2">
                    <p className="font-ibm-plex text-sm font-medium truncate">{d.description || d.filename}</p>
                    <p className="text-xs text-[#71717A] font-ibm-plex">
                      {d.category ? <span className="bg-[#F4F4F5] px-1.5 py-0.5 rounded mr-1">{d.category}</span> : null}
                      {d.filename}{d._shared ? " (Condiviso)" : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <a href={`${API_URL}/api/portal/documents/preview/${d.id}`} target="_blank" rel="noreferrer" title="Anteprima">
                      <Button variant="ghost" size="icon" className="h-7 w-7">
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                    </a>
                    <a href={`${API_URL}/api/portal/documents/download/${d.id}`} target="_blank" rel="noreferrer" title="Scarica">
                      <Button variant="ghost" size="icon" className="h-7 w-7">
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                    </a>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDeleteDoc(d.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Document with Category Dialog */}
      <Dialog open={!!uploadDocEmp} onOpenChange={(open) => { if (!open) { setUploadDocEmp(null); setUploadDocForm({ category: "", description: "", file: null }); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit">Carica Documento per {uploadDocEmp?.last_name} {uploadDocEmp?.first_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label className="text-xs font-ibm-plex font-medium">Categoria *</Label>
              <Select value={uploadDocForm.category || "none"} onValueChange={(v) => setUploadDocForm({...uploadDocForm, category: v === "none" ? "" : v})}>
                <SelectTrigger className="font-ibm-plex"><SelectValue placeholder="Seleziona categoria" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Seleziona categoria...</SelectItem>
                  {EMP_DOC_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-ibm-plex font-medium">Descrizione</Label>
              <Input value={uploadDocForm.description} onChange={(e) => setUploadDocForm({...uploadDocForm, description: e.target.value})} className="font-ibm-plex" placeholder="Es. Busta paga Maggio 2026" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-ibm-plex font-medium">File *</Label>
              <Input type="file" onChange={(e) => setUploadDocForm({...uploadDocForm, file: e.target.files[0] || null})} className="font-ibm-plex" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDocEmp(null)} className="font-ibm-plex">Annulla</Button>
            <Button
              disabled={!uploadDocForm.file || !uploadDocForm.category}
              onClick={async () => {
                if (uploadDocEmp && uploadDocForm.file) {
                  await handleUploadDoc(uploadDocEmp.id, uploadDocForm.file, uploadDocForm.description, uploadDocForm.category);
                  setUploadDocEmp(null);
                  setUploadDocForm({ category: "", description: "", file: null });
                }
              }}
              className="bg-black hover:bg-gray-800 text-white font-ibm-plex"
            >
              Carica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
